/**
 * Pulling a storm history report, and everything the job page does with one.
 *
 * The work is split the way it is for one reason: the National Weather Service
 * archive sends no CORS header, so this page cannot read its response however
 * politely it asks. So the browser decides *what* to ask for — core works out
 * the bounding box, the review period and, after the first round, which days
 * are worth a verification query — and the `storm-report` Edge Function does the
 * asking. The function accepts no URL, only boxes and dates; see the note at the
 * top of `supabase/functions/storm-report/queries.ts` for why that distinction
 * is the whole security design.
 *
 * It is two rounds rather than one, and the second is what makes the document
 * worth sending. The first pull says which days produced hail near the property.
 * The second asks the archive for each of those days on its own, so the report
 * can print a query URL beside every row *and* state how many records that query
 * returns — which is what lets an adjuster check a line rather than take it on
 * trust.
 */

import {
  ALABAMA_BIAS,
  PHOTON_URL,
  boundingBox,
  searchRadius,
  buildStormReport,
  eventDays,
  nextDay,
  validateStormSearch,
  yearsBefore,
  type Job,
  type Origin,
  type StormAddress,
  type StormReportSnapshot,
  type StormSearch,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

export class StormReportError extends Error {}

/**
 * How many event days get a second, corroborating query.
 *
 * Every one is a separate request to the Iowa Environmental Mesonet, which is a
 * free public archive run by a university; forty is generous for a normal
 * search and is well under the Edge Function's own ceiling. Days past it keep
 * their citation and lose only the row count beside it.
 */
export const MAX_VERIFIED_DAYS = 40;

interface DateRange {
  start: string;
  end: string;
}

/**
 * Call the function, keeping the sentence it sent back.
 *
 * `functions.invoke` reports a non-2xx as a generic error and hides the body on
 * `context`. The body is where the useful sentence is — "the archive is busy",
 * "that area is too large" — and a rep shown "non-2xx status code" instead has
 * been told nothing at all.
 */
async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("storm-report", {
    body,
  });
  if (!error) return data as T;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = await context.json().catch(() => null);
    if (payload && typeof payload.error === "string") {
      throw new StormReportError(payload.error);
    }
  }

  // Everything else is the transport failing rather than the server answering,
  // and the client's own words for that are "Failed to send a request to the
  // Edge Function" — which tells a rep standing on a driveway nothing they can
  // do anything about.
  throw new StormReportError(
    "Could not reach the storm data service. Check the signal and try again.",
  );
}

const fetchCsv = async (
  bbox: ReturnType<typeof boundingBox>,
  ranges: DateRange[],
): Promise<string[]> => {
  const { csv } = await invoke<{ csv: string[] }>({
    action: "fetch",
    bbox,
    ranges,
  });
  return csv;
};

/** The address as it should read on the document. */
export function stormAddress(
  job: Job,
  county: string | null = null,
): StormAddress {
  const line = [job.address, job.city, job.state].filter(Boolean).join(", ");
  return {
    full: [line, job.zip].filter(Boolean).join(" ").trim(),
    // Never guessed from the county of the nearest hail report — that would
    // state where the hail fell rather than where the roof is. Either the
    // geocoder names it or the document prints an em dash.
    county,
  };
}

/** The whole address as one line, for a geocoder to resolve. */
export const addressQuery = (job: Job): string =>
  [job.address, job.city, job.state, job.zip].filter(Boolean).join(", ");

export interface ResolvedProperty {
  origin: Origin;
  county: string | null;
}

/**
 * Where the property is, and which county it is in.
 *
 * Coordinates are stored on the job when the lead was created from the address
 * suggestions — but plenty of jobs predate that, or were typed in, and a button
 * that is greyed out on most of the job list is not a feature. So an address
 * with no coordinates is resolved here instead, against the same Photon service
 * the address box already uses, which is why it is reachable from the browser at
 * all when the weather archive is not.
 *
 * The county is best-effort in both cases. It appears on the document's
 * identification strip and nowhere that a number depends on it, so a geocoder
 * that is slow or unhelpful costs an em dash and not the report.
 */
export async function resolveProperty(job: Job): Promise<ResolvedProperty> {
  const stored =
    typeof job.lat === "number" && typeof job.lng === "number"
      ? { lat: job.lat, lng: job.lng }
      : null;

  const found = await geocode(addressQuery(job));

  if (stored) return { origin: stored, county: found?.county ?? null };
  if (found) return { origin: found.origin, county: found.county };

  throw new StormReportError(
    "That address could not be placed on the map, so there is nothing to measure distances from. Check the address on the customer file.",
  );
}

/** How long to wait on the geocoder before giving up on the county. */
const GEOCODE_TIMEOUT_MS = 8000;

async function geocode(
  query: string,
): Promise<{ origin: Origin; county: string | null } | null> {
  if (!query.trim()) return null;

  const params = new URLSearchParams({
    q: query,
    limit: "1",
    lang: "en",
    lat: String(ALABAMA_BIAS.lat),
    lon: String(ALABAMA_BIAS.lon),
    zoom: String(ALABAMA_BIAS.zoom),
    location_bias_scale: String(ALABAMA_BIAS.location_bias_scale),
  });

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), GEOCODE_TIMEOUT_MS);
  try {
    const response = await fetch(`${PHOTON_URL}?${params.toString()}`, {
      signal: abort.signal,
    });
    const body = (await response.json()) as {
      features?: {
        properties?: { county?: string };
        geometry?: { coordinates?: [number, number] };
      }[];
    };
    const feature = body.features?.[0];
    const coordinates = feature?.geometry?.coordinates;
    if (!coordinates) return null;
    const [lng, lat] = coordinates;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      origin: { lat, lng },
      county: cleanCounty(feature?.properties?.county),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** "Jefferson County" reads as "Jefferson · BMX" beside the NWS office. */
const cleanCounty = (value: string | undefined): string | null => {
  const trimmed = (value ?? "").replace(/\s+County$/i, "").trim();
  return trimmed || null;
};

/** Today, as the browser's calendar has it. */
export const today = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/**
 * Why this job cannot have a report pulled for it, if it cannot.
 *
 * Only the address. Coordinates are looked up at pull time when the job has
 * none — see `resolveProperty` — so an older job is not shut out of the feature
 * for having been created before the address box stored them.
 */
export function stormReportBlocker(job: Job): string | null {
  if (!job.address?.trim()) {
    return "This job has no address on it yet. Add one on the customer file and the storm history can be pulled.";
  }
  return null;
}

/**
 * Pull a report. Two rounds, as described at the top of the file.
 */
export async function pullStormReport(
  job: Job,
  search: StormSearch,
): Promise<StormReportSnapshot> {
  const blocker = stormReportBlocker(job);
  if (blocker) throw new StormReportError(blocker);

  const problems = validateStormSearch(search);
  if (problems[0]) throw new StormReportError(problems[0].message);

  const { origin, county } = await resolveProperty(job);
  const address = stormAddress(job, county);
  const now = today();
  const bbox = boundingBox(origin, searchRadius(search));

  const [masterCsv] = await fetchCsv(bbox, [
    { start: yearsBefore(now, search.years), end: now },
  ]);

  const first = buildStormReport({
    origin,
    address,
    search,
    today: now,
    masterCsv: masterCsv ?? "",
  });

  const days = eventDays(first);
  if (days.length === 0) return first;

  // Wind pushed this from a handful of days to dozens: the reference property
  // has six hail days inside five miles and nineteen wind days inside two. Each
  // one is a separate request to a free archive run by a university, so the
  // second round is capped. Days beyond the cap still get their query URL
  // printed beside their rows — the URL is built, not fetched — they simply do
  // not carry a row count, which costs a sentence of corroboration and nothing
  // that can be checked.
  const verified = days.slice(0, MAX_VERIFIED_DAYS);

  // Second round. A failure here costs the row counts beside each citation and
  // nothing else — the query URLs are built, not fetched — so it is not worth
  // losing the whole report over.
  let dayCsv: Record<string, string> | undefined;
  try {
    const bodies = await fetchCsv(
      bbox,
      verified.map((day) => ({ start: day, end: nextDay(day) })),
    );
    dayCsv = Object.fromEntries(
      verified.map((day, i) => [day, bodies[i] ?? ""]),
    );
  } catch {
    dayCsv = undefined;
  }

  return buildStormReport({
    origin,
    address,
    search,
    today: now,
    masterCsv: masterCsv ?? "",
    dayCsv,
  });
}

// ------------------------------------------------------------- storage ---

export interface StormReportRow {
  id: string;
  token: string;
  status: "ready" | "sent" | "void";
  years: number;
  radius_miles: number;
  threshold_inches: number;
  /** Null on reports pulled before the report covered wind at all. */
  wind_radius_miles: number | null;
  event_count: number;
  sent_at: string | null;
  opened_at: string | null;
  open_count: number;
  created_at: string;
  snapshot: StormReportSnapshot;
}

const ROW_COLUMNS = `
  id,token,status,years,radius_miles,threshold_inches,wind_radius_miles,event_count,
  sent_at,opened_at,open_count,created_at,snapshot
`;

/** Numeric columns come back from PostgREST as strings. */
const asRow = (row: Record<string, unknown>): StormReportRow => ({
  ...(row as unknown as StormReportRow),
  years: Number(row.years),
  radius_miles: Number(row.radius_miles),
  threshold_inches: Number(row.threshold_inches),
  wind_radius_miles:
    row.wind_radius_miles === null || row.wind_radius_miles === undefined
      ? null
      : Number(row.wind_radius_miles),
  event_count: Number(row.event_count ?? 0),
  open_count: Number(row.open_count ?? 0),
});

export async function fetchStormReports(
  jobId: string,
): Promise<StormReportRow[]> {
  const rows = unwrap<Record<string, unknown>[]>(
    await supabase
      .from("storm_reports")
      .select(ROW_COLUMNS)
      .eq("job_id", jobId)
      .order("created_at", { ascending: false }),
  );
  return rows.map(asRow);
}

/**
 * Keep a pulled report.
 *
 * The snapshot goes in whole and is never rewritten — a database trigger
 * refuses. What was sent to a carrier has to stay readable as it was sent, and
 * Local Storm Reports are revised in quality control, so re-running the query is
 * not a way of reproducing it. See ADR 0043.
 */
export async function saveStormReport(
  jobId: string,
  snapshot: StormReportSnapshot,
  profileId: string | null,
): Promise<StormReportRow> {
  const rows = unwrap<Record<string, unknown>[]>(
    await supabase
      .from("storm_reports")
      .insert({
        job_id: jobId,
        years: snapshot.search.years,
        radius_miles: snapshot.search.radiusMiles,
        threshold_inches: snapshot.search.thresholdInches,
        wind_radius_miles: snapshot.search.windRadiusMiles,
        snapshot,
        created_by: profileId,
      })
      .select(ROW_COLUMNS),
  );
  return asRow(rows[0]!);
}

/**
 * Record that the report went out.
 *
 * The database stamps the date itself, so a caller cannot send one and leave it
 * looking unsent, or claim a date it was not sent on.
 */
export async function markStormReportSent(id: string): Promise<void> {
  unwrap(
    await supabase
      .from("storm_reports")
      .update({ status: "sent" })
      .eq("id", id),
  );
}

/** Kill a link without losing the record of what was sent. */
export async function voidStormReport(id: string): Promise<void> {
  unwrap(
    await supabase
      .from("storm_reports")
      .update({ status: "void" })
      .eq("id", id),
  );
}

/** The adjuster's URL for a report. */
export const stormReportUrl = (token: string): string =>
  `${window.location.origin}/s/${token}`;
